/**
 * Procedural body system — pure data (and the small math primitives those
 * data tables use). All bone offsets are expressed as Euler rotations in
 * bone-local space, in radians, additive on top of whatever the mixer is
 * driving for that bone.
 *
 * This module is intentionally side-effect free: it exports types,
 * constants, and pure functions. The runtime that consumes it lives in
 * `figure.ts` (bone discovery + per-frame integration).
 *
 * ── How offsets compose at runtime (per bone, per frame) ───────────────────
 *
 *   final_rotation = baseline_after_mixer
 *                  + body_state_offset (continuous)
 *                  + body_gesture_offset (one-shot envelope)
 *                  + Σ procedural_hold_offset (sustained, fade in/out)
 *
 * The summation is on Euler axes (x/y/z), then re-encoded as the bone's
 * rotation. For the small angles we use (≤ ~0.6 rad) the Euler-additive
 * approximation is visually indistinguishable from a proper quaternion
 * compose, and the cost is one add per axis.
 *
 * ── Why hand-tune offsets, not solve them ────────────────────────────────
 *
 * Hand-authored offsets are fast (microseconds), have zero dependencies,
 * and never produce a "wrong" pose because the bones can't be asked to
 * touch each other. The trade-off is that fine articulation
 * (e.g. fingers wrapping a specific contact point) is out of scope. For
 * holds that need contact (hand-on-chin), use a 2-bone IK solver later.
 */

// ── Bone slot vocabulary ──────────────────────────────────────────────────
//
// Semantic names for the Mixamo skeleton chain. Every slot maps 1:1 to a
// Mixamo bone via BONE_PATTERNS below. The slot vocabulary is what we
// reference in profile data so the offset tables read like body language
// rather than bone-name soup.
//
// Mixamo native names:
//   "Shoulder" = clavicle (we call it `clavicleL/R`)
//   "Arm"      = upper arm (we call it `upperArmL/R`)
//   "ForeArm"  = forearm (we call it `forearmL/R`)
//   "UpLeg"    = thigh (we call it `upLegL/R`)
//   "Leg"      = shin (we call it `legL/R`)

export type BoneSlot =
  | 'hips'
  | 'spine'
  | 'spine1'
  | 'spine2'
  | 'neck'
  | 'clavicleL'
  | 'clavicleR'
  | 'upperArmL'
  | 'upperArmR'
  | 'forearmL'
  | 'forearmR'
  | 'handL'
  | 'handR'
  | 'upLegL'
  | 'upLegR'
  | 'legL'
  | 'legR'
  | 'footL'
  | 'footR';

/**
 * Regex per bone slot to find the matching Mixamo bone in the runtime
 * skeleton. Patterns are case-insensitive and tolerate the `mixamorig`
 * prefix being present or absent. Anchoring at end-of-name (`$`) prevents
 * matching `LeftArm` when looking for `Spine`.
 */
export const BONE_PATTERNS: Record<BoneSlot, RegExp> = {
  hips:       /^(?:mixamorig)?Hips$/i,
  spine:      /^(?:mixamorig)?Spine$/i,
  spine1:     /^(?:mixamorig)?Spine1$/i,
  spine2:     /^(?:mixamorig)?Spine2$/i,
  neck:       /^(?:mixamorig)?Neck$/i,
  clavicleL:  /^(?:mixamorig)?LeftShoulder$/i,
  clavicleR:  /^(?:mixamorig)?RightShoulder$/i,
  upperArmL:  /^(?:mixamorig)?LeftArm$/i,
  upperArmR:  /^(?:mixamorig)?RightArm$/i,
  forearmL:   /^(?:mixamorig)?LeftForeArm$/i,
  forearmR:   /^(?:mixamorig)?RightForeArm$/i,
  handL:      /^(?:mixamorig)?LeftHand$/i,
  handR:      /^(?:mixamorig)?RightHand$/i,
  upLegL:     /^(?:mixamorig)?LeftUpLeg$/i,
  upLegR:     /^(?:mixamorig)?RightUpLeg$/i,
  legL:       /^(?:mixamorig)?LeftLeg$/i,
  legR:       /^(?:mixamorig)?RightLeg$/i,
  footL:      /^(?:mixamorig)?LeftFoot$/i,
  footR:      /^(?:mixamorig)?RightFoot$/i,
};

export const ALL_BONE_SLOTS = Object.keys(BONE_PATTERNS) as BoneSlot[];

// ── Euler offset shape ────────────────────────────────────────────────────
//
// Every component optional so a profile only has to mention axes it cares
// about. The runtime defaults missing axes to 0.

export interface EulerOffset {
  x?: number;
  y?: number;
  z?: number;
}

/** Per-bone offset table. Slots not mentioned default to identity. */
export type BonePose = Partial<Record<BoneSlot, EulerOffset>>;

// ── Math primitives (duplicated from figure.ts for module cohesion) ───────

/**
 * Trapezoidal envelope: ramp 0→1 over `attack`, hold 1 for `hold`, ramp
 * 1→0 over `decay`. Returns 0 outside the window.
 */
export function trapezoid(t: number, attack: number, hold: number, decay: number): number {
  if (t < 0) return 0;
  if (t < attack) return t / attack;
  if (t < attack + hold) return 1;
  if (t < attack + hold + decay) return 1 - (t - attack - hold) / decay;
  return 0;
}

/**
 * Damped oscillator: sine at `freq` Hz that fades out linearly between
 * `fadeStart` and `fadeEnd`. Used for shake/wobble layered on top of
 * gesture envelopes.
 */
export function dampedOsc(t: number, freq: number, fadeStart: number, fadeEnd: number): number {
  if (t < 0 || t > fadeEnd) return 0;
  const env = t < fadeStart ? 1 : Math.max(0, 1 - (t - fadeStart) / (fadeEnd - fadeStart));
  return Math.sin(2 * Math.PI * freq * t) * env;
}

// ── Body gesture vocabulary ───────────────────────────────────────────────
//
// Transient one-shot body actions. Each has a duration and a sample(t)
// that returns the per-bone offsets at time t. Profiles auto-clear once
// `t > duration`. Body gestures run on a slot independent of face
// gestures so they can layer: face=gasp + body=recoil_step works.

export type BodyGesture =
  | 'shoulder_shrug'
  | 'shoulder_roll'
  | 'weight_shift'
  | 'glance_over_shoulder'
  | 'chest_inhale_deep'
  | 'chest_deflate'
  | 'arm_brace_self'
  | 'hand_to_neck_rub'
  | 'lean_in'
  | 'lean_back'
  | 'recoil_step'
  | 'flinch_body'
  | 'head_tilt_curious'
  | 'nervous_finger_fidget'
  | 'foot_tap';

export interface BodyGestureFrame {
  bones?: BonePose;
}

export interface BodyGestureProfile {
  duration: number;
  sample(t: number): BodyGestureFrame;
}

// Convenience helpers for axis-only offsets — keeps profile authoring terse.
const rx = (v: number): EulerOffset => ({ x: v });
const ry = (v: number): EulerOffset => ({ y: v });
const rz = (v: number): EulerOffset => ({ z: v });
const rxyz = (x: number, y: number, z: number): EulerOffset => ({ x, y, z });

export const BODY_GESTURE_PROFILES: Record<BodyGesture, BodyGestureProfile> = {
  // 100 ms attack, 250 ms hold, 350 ms decay. Both clavicles lift toward
  // the ears; small head retract because shrugging draws the head down.
  shoulder_shrug: {
    duration: 0.7,
    sample(t) {
      const e = trapezoid(t, 0.1, 0.25, 0.35);
      return {
        bones: {
          // Clavicle local Z lifts shoulder toward ear in Mixamo's rest.
          clavicleL: rz(0.18 * e),
          clavicleR: rz(-0.18 * e),
          neck: rx(-0.05 * e),
        },
      };
    },
  },

  // Single shoulder roll: clavicle rolls back-up-forward-down. Asymmetric:
  // right shoulder leads, left echoes 200 ms later. Reads as a tension
  // release without looking choreographed.
  shoulder_roll: {
    duration: 1.0,
    sample(t) {
      const wave = (phase: number): number => {
        if (phase < 0 || phase > 0.8) return 0;
        return Math.sin((phase / 0.8) * Math.PI);
      };
      const r = wave(t);
      const l = wave(t - 0.2);
      return {
        bones: {
          clavicleR: { z: -0.15 * r, x: 0.10 * r },
          clavicleL: { z:  0.15 * l, x: 0.10 * l },
        },
      };
    },
  },

  // Subtle pelvis-and-counter-spine sway. ~1.4 s. Reads as "shifting
  // weight to the other foot" — essential for rooted standing scenes.
  weight_shift: {
    duration: 1.4,
    sample(t) {
      const phase = t / 1.4;
      const swing = Math.sin(phase * Math.PI); // 0 → 1 → 0
      const dir = 1; // could randomise sign at trigger time
      return {
        bones: {
          // Hips yaw slightly toward the loaded side; spine counter-rotates
          // so the head stays roughly forward.
          hips:   ry(0.06 * swing * dir),
          spine1: ry(-0.03 * swing * dir),
          spine2: ry(-0.02 * swing * dir),
          // Loaded leg straightens, free leg slightly bends — captured by
          // the upLeg pitch difference.
          upLegL: rx(0.04 * swing * dir),
          upLegR: rx(-0.04 * swing * dir),
        },
      };
    },
  },

  // Head + spine yaw to look behind. Held 600 ms then returns. Use for
  // recognition / paranoia / "did I hear something?" beats.
  glance_over_shoulder: {
    duration: 1.4,
    sample(t) {
      const e = trapezoid(t, 0.25, 0.55, 0.6);
      return {
        bones: {
          spine2: ry(-0.18 * e),
          spine1: ry(-0.10 * e),
          neck:   ry(-0.12 * e),
          // Head bone is touched here too; the figure runtime adds it to
          // the existing gesture-head-offset path so it composes with the
          // procedural noise.
        },
      };
    },
  },

  // Slow inhale: clavicles lift, spine straightens slightly, chest opens.
  // Held briefly, then a slow release. ~1.6 s total.
  chest_inhale_deep: {
    duration: 1.6,
    sample(t) {
      const inhale = trapezoid(t, 0.6, 0.3, 0.7);
      return {
        bones: {
          clavicleL: rz( 0.06 * inhale),
          clavicleR: rz(-0.06 * inhale),
          spine1:    rx(-0.04 * inhale),
          spine2:    rx(-0.04 * inhale),
        },
      };
    },
  },

  // Mirror of inhale: shoulders drop, spine slumps forward, head slightly
  // tips down. The "giving up" exhale.
  chest_deflate: {
    duration: 1.6,
    sample(t) {
      const fall = trapezoid(t, 0.4, 0.4, 0.8);
      return {
        bones: {
          clavicleL: rz(-0.05 * fall),
          clavicleR: rz( 0.05 * fall),
          spine:     rx( 0.05 * fall),
          spine1:    rx( 0.05 * fall),
          neck:      rx( 0.06 * fall),
        },
      };
    },
  },

  // Both upper arms pull inward, forearms cross-body slightly. NOT a
  // closed arms-crossed pose — that's a hold. This is just the
  // self-protective hug-yourself gesture.
  arm_brace_self: {
    duration: 1.5,
    sample(t) {
      const e = trapezoid(t, 0.3, 0.4, 0.8);
      return {
        bones: {
          // Upper arm rotation (in toward chest)
          upperArmL: rxyz(0,  0.30 * e,  0.10 * e),
          upperArmR: rxyz(0, -0.30 * e, -0.10 * e),
          // Forearm bend (~50°)
          forearmL: ry( 0.85 * e),
          forearmR: ry(-0.85 * e),
          clavicleL: rz( 0.05 * e),
          clavicleR: rz(-0.05 * e),
        },
      };
    },
  },

  // Right hand drifts up to the back-of-neck zone. We don't actually
  // touch the neck (no IK), but the silhouette reads as the gesture.
  hand_to_neck_rub: {
    duration: 1.4,
    sample(t) {
      const e = trapezoid(t, 0.35, 0.45, 0.6);
      // Slow side-to-side rub during the hold portion
      const rub = e > 0.5 ? Math.sin(t * 8) * 0.04 : 0;
      return {
        bones: {
          upperArmR: rxyz(-0.10 * e, -0.20 * e, -0.45 * e),
          forearmR:  ry(-1.40 * e + rub),
          handR:     rxyz(0.10 * e, 0, 0),
          neck:      rx(-0.04 * e),
        },
      };
    },
  },

  // Spine pitches forward, head follows. ~1 s.
  lean_in: {
    duration: 1.2,
    sample(t) {
      const e = trapezoid(t, 0.35, 0.5, 0.35);
      return {
        bones: {
          spine:  rx(0.05 * e),
          spine1: rx(0.05 * e),
          spine2: rx(0.04 * e),
          neck:   rx(0.04 * e),
        },
      };
    },
  },

  // Spine pitches back; hips don't move (rooted). Reads as skeptical
  // disengagement.
  lean_back: {
    duration: 1.2,
    sample(t) {
      const e = trapezoid(t, 0.35, 0.5, 0.35);
      return {
        bones: {
          spine:  rx(-0.06 * e),
          spine1: rx(-0.05 * e),
          spine2: rx(-0.04 * e),
          neck:   rx(-0.03 * e),
        },
      };
    },
  },

  // Reflexive recoil: hips pitch back, chest pulls, shoulders rise. NO
  // foot motion (we can't safely move feet without IK).
  recoil_step: {
    duration: 0.7,
    sample(t) {
      const e = trapezoid(t, 0.08, 0.12, 0.5);
      return {
        bones: {
          hips:      rx(-0.05 * e),
          spine:     rx(-0.04 * e),
          spine1:    rx(-0.04 * e),
          clavicleL: rz( 0.10 * e),
          clavicleR: rz(-0.10 * e),
          neck:      rx(-0.06 * e),
        },
      };
    },
  },

  // Body-half of a flinch — pairs with face flinch. Shoulders raise,
  // spine compresses briefly. 600 ms.
  flinch_body: {
    duration: 0.6,
    sample(t) {
      const e = trapezoid(t, 0.05, 0.1, 0.45);
      return {
        bones: {
          clavicleL: rz( 0.20 * e),
          clavicleR: rz(-0.20 * e),
          spine1:    rx( 0.04 * e),
          neck:      rx(-0.05 * e),
        },
      };
    },
  },

  // Head tilts to one side. Clavicles do nothing (the head-tilt is the
  // entire reading). 800 ms.
  head_tilt_curious: {
    duration: 0.9,
    sample(t) {
      const e = trapezoid(t, 0.2, 0.4, 0.3);
      return {
        bones: {
          neck: rz(0.18 * e),
        },
      };
    },
  },

  // 5 Hz hand oscillation — thumb-against-fingers fidget visualised at
  // the wrist scale. Doesn't move arms, only hand.
  nervous_finger_fidget: {
    duration: 1.6,
    sample(t) {
      const env = t < 1.4 ? 1 : Math.max(0, 1 - (t - 1.4) / 0.2);
      const wob = Math.sin(2 * Math.PI * 5 * t) * env;
      return {
        bones: {
          handR: rxyz(0, wob * 0.05, wob * 0.04),
          handL: rxyz(0, wob * 0.04, wob * 0.04),
        },
      };
    },
  },

  // Right foot ankle pitches up/down at 2.5 Hz — impatient toe-tap
  // without translating the foot.
  foot_tap: {
    duration: 1.6,
    sample(t) {
      const env = t < 1.4 ? 1 : Math.max(0, 1 - (t - 1.4) / 0.2);
      const tap = Math.max(0, Math.sin(2 * Math.PI * 2.5 * t)) * env;
      return {
        bones: {
          footR: rx(-0.18 * tap),
        },
      };
    },
  },
};

export const ALL_BODY_GESTURES = Object.keys(BODY_GESTURE_PROFILES) as BodyGesture[];

// ── Body state vocabulary ─────────────────────────────────────────────────
//
// Sustained postural modes. Each defines a constant offset bias plus
// optional per-frame procedural noise. The runtime lerps from current
// state offsets to target state offsets over BODY_STATE_LERP_SEC, so
// switching states is smooth. State offsets stack additively with body
// gestures and procedural holds.

export type BodyState =
  | 'neutral_idle'
  | 'restless_idle'
  | 'slumped_idle'
  | 'tense_idle'
  | 'confident_idle'
  | 'withdrawn_idle';

export interface BodyStateProfile {
  /** Constant offset applied as long as the state is active. */
  bias: BonePose;
  /**
   * Optional procedural per-frame offset. `t` is wall time in seconds.
   * Use sum-of-sines on irrational frequencies for non-repeating noise.
   * Intensity should already be baked in; the runtime applies as-is.
   */
  noise?: (t: number) => BonePose;
}

export const BODY_STATE_PROFILES: Record<BodyState, BodyStateProfile> = {
  // Identity: zero bias, zero noise. Baseline that everything else
  // crossfades from / to.
  neutral_idle: {
    bias: {},
  },

  // Anxious shifting weight: hips yaw oscillates at very low frequency,
  // weight bias drifts slowly. No big rotations — just enough to read as
  // not-still.
  restless_idle: {
    bias: {},
    noise: (t) => {
      const slow = Math.sin(t * 0.27) * 0.03;
      const fast = Math.sin(t * 1.1 + 0.5) * 0.012;
      return {
        hips:    ry(slow + fast),
        spine1:  ry(-(slow + fast) * 0.6),
        upLegL:  rx(Math.sin(t * 0.31) * 0.02),
        upLegR:  rx(-Math.sin(t * 0.31) * 0.02),
      };
    },
  },

  // Sad / depressed: shoulders drop, spine slumps forward, head tips
  // down. Noise is reduced — slumped people hold still.
  slumped_idle: {
    bias: {
      clavicleL: rz(-0.07),
      clavicleR: rz( 0.07),
      spine:     rx( 0.04),
      spine1:    rx( 0.04),
      spine2:    rx( 0.03),
      neck:      rx( 0.05),
    },
  },

  // Fearful / on-edge: shoulders raised, spine slightly straightened,
  // breathing reduced (figure handles breathing weight elsewhere; here
  // we just bias posture).
  tense_idle: {
    bias: {
      clavicleL: rz( 0.08),
      clavicleR: rz(-0.08),
      spine1:    rx(-0.02),
      spine2:    rx(-0.02),
    },
    // Tiny high-frequency tremor on the hands.
    noise: (t) => ({
      handL: rxyz(Math.sin(t * 7.3) * 0.01, 0, 0),
      handR: rxyz(Math.sin(t * 7.7 + 1.1) * 0.01, 0, 0),
    }),
  },

  // Proud / at ease: spine straight, clavicles back, slight chin lift.
  confident_idle: {
    bias: {
      clavicleL: rz(-0.04),
      clavicleR: rz( 0.04),
      // Pull clavicles slightly back via x-axis (mixamo "shoulders back")
      spine1:    rx(-0.03),
      spine2:    rx(-0.02),
      neck:      rx(-0.03),
    },
  },

  // Shy / embarrassed: shoulders inward, spine slightly forward, head
  // ducked. Less extreme than slumped — closer to "trying to take less
  // space" than to "defeated".
  withdrawn_idle: {
    bias: {
      clavicleL: rz( 0.05),
      clavicleR: rz(-0.05),
      // Shoulders inward (clavicles also rotate forward on Y)
      // Mixamo Y axis on clavicle moves the shoulder ant/post.
      spine1:    rx( 0.02),
      neck:      rx( 0.03),
    },
  },
};

export const ALL_BODY_STATES = Object.keys(BODY_STATE_PROFILES) as BodyState[];

// ── Hand-authored hold poses ──────────────────────────────────────────────
//
// Replaces the Uthana-generated hold GLBs with deterministic, free,
// instant-to-activate static poses. Each pose is a per-bone Euler offset
// table; the runtime applies it with an attack/sustain/release envelope
// so the character "moves into" the pose smoothly.
//
// Names match the existing HOLD_NAMES catalog so the LLM (and the rest of
// the pipeline) is unchanged. The runtime detects these names and skips
// the GLB load path entirely.

export type ProceduralHoldName =
  | 'arms_crossed'
  | 'hand_on_hip_right'
  | 'hand_on_hip_left'
  | 'hands_on_hips'
  | 'hands_clasped_front'
  | 'hands_clasped_behind_back'
  | 'hands_clasped_behind_head'
  | 'hand_in_pocket_right'
  | 'arms_relaxed'
  // Back-compat aliases — the existing server holds.ts catalogue uses
  // these older keys. The runtime maps them to the new procedural poses
  // so the LLM can keep emitting the names it's been trained on.
  | 'hand_on_hip'
  | 'hands_behind_back';

export const PROCEDURAL_HOLD_POSES: Record<ProceduralHoldName, BonePose> = {
  // Both arms fold across chest. Forearms rotate ~90° at the elbows;
  // upper arms rotate inward + slightly down so the elbows tuck close.
  // The hands settle on the opposite upper arms (no IK contact, but the
  // silhouette reads as crossed).
  //
  // Calibrated against the live mixer (Standing Idle) via the tuning
  // panel; offsets are deltas from the idle pose, applied on top of the
  // anchor captured at activation. Asymmetric values reflect minor rig
  // asymmetries — pure mirror sometimes reads as visibly artificial.
  arms_crossed: {
    clavicleL: rz( 0.040),
    clavicleR: { x: -0.470, z: -0.040 },
    upperArmL: rxyz( 0.030,  0.060,  0.370),
    upperArmR: rxyz( 0.290, -1.130, -0.150),
    forearmL:  rxyz( 0.160,  0.860,  1.110),
    forearmR:  rxyz( 0.070, -0.490, -1.220),
    handL:     rxyz( 0.410,  0.750,  0.290),
    handR:     rxyz(-0.320,  0.070,  0.100),
  },

  // Right hand on right hip, elbow out. Upper arm rotates back-and-out,
  // forearm bends ~90° forward, hand rotates to lay against hip.
  hand_on_hip_right: {
    upperArmR: rxyz( 0.10, -0.45, -0.30),
    forearmR:  ry( -1.55),
    handR:     rxyz( 0.20, -0.10,  0.10),
    clavicleR: rz(-0.06),
  },

  hand_on_hip_left: {
    upperArmL: rxyz( 0.10,  0.45,  0.30),
    forearmL:  ry(  1.55),
    handL:     rxyz( 0.20,  0.10, -0.10),
    clavicleL: rz( 0.06),
  },

  // Both hands on hips — symmetric power-pose.
  hands_on_hips: {
    upperArmL: rxyz( 0.10,  0.45,  0.30),
    forearmL:  ry(  1.55),
    handL:     rxyz( 0.20,  0.10, -0.10),
    upperArmR: rxyz( 0.10, -0.45, -0.30),
    forearmR:  ry( -1.55),
    handR:     rxyz( 0.20, -0.10,  0.10),
    clavicleL: rz( 0.06),
    clavicleR: rz(-0.06),
  },

  // Hands together at navel level. Both forearms rotate ~70° inward,
  // wrists meet near the centreline.
  hands_clasped_front: {
    upperArmL: rxyz( 0.30,  0.20,  0.20),
    forearmL:  ry(  1.10),
    handL:     rxyz( 0,     0.10,  0),
    upperArmR: rxyz( 0.30, -0.20, -0.20),
    forearmR:  ry( -1.10),
    handR:     rxyz( 0,    -0.10,  0),
  },

  // Hands clasped behind back — formal/stoic. Extracted from "Arms Behind Back" Mixamo clip @ 1.0s.
  hands_clasped_behind_back: {
    clavicleL: rxyz( 0.318,  0.280, -0.066),
    clavicleR: rxyz( 0.227, -0.405,  0.137),
    upperArmL: rxyz(-2.189,  1.955,  2.223),
    upperArmR: rxyz(-2.617, -1.513,  2.886),
    forearmL:  rxyz( 0.270, -0.343, -0.346),
    forearmR:  rxyz(-0.121, -0.052, -0.246),
    handL:     rxyz(-0.430,  0.424,  0.294),
    handR:     rxyz(-0.057,  0.575,  0.160),
  },

  // Right hand behind head — relaxed-confident or "thinking it over".
  // Extracted from "Right Hand Behind Head" Mixamo clip @ 1.0s.
  // Left arm stays in a natural relaxed position. Mirror L→R in the
  // tuning panel if you want both hands behind the head.
  hands_clasped_behind_head: {
    clavicleL: rxyz(-0.798,  0.436,  0.199),
    clavicleR: rxyz(-0.930, -0.544, -0.365),
    upperArmL: rxyz( 0.131, -0.148,  0.100),
    upperArmR: rxyz(-1.039,  0.898, -0.235),
    forearmL:  rxyz( 0.296,  0.274,  0.994),
    forearmR:  rxyz( 0.254, -0.070, -1.744),
    handL:     rxyz( 0.480,  0.880,  0.445),
    handR:     rxyz(-0.162,  0.448, -0.171),
  },

  // Right hand at hip-pocket level — relaxed casual. Upper arm hangs,
  // forearm bends slightly inward as if entering a pocket. No actual
  // contact — the silhouette suggests the gesture.
  hand_in_pocket_right: {
    upperArmR: rxyz( 0.10, -0.15, -0.10),
    forearmR:  ry( -0.55),
    handR:     rxyz( 0.10, -0.20,  0.05),
  },

  // Explicit neutral — overrides any drift from previous holds. Identity
  // pose. Useful for "release everything" beats.
  arms_relaxed: {},

  // ── Aliases ────────────────────────────────────────────────────────────
  hand_on_hip: {
    upperArmR: rxyz( 0.10, -0.45, -0.30),
    forearmR:  ry( -1.55),
    handR:     rxyz( 0.20, -0.10,  0.10),
    clavicleR: rz(-0.06),
  },
  hands_behind_back: {
    upperArmL: rxyz(-0.30,  0.10,  0.10),
    forearmL:  ry(  0.90),
    handL:     rxyz( 0.10, -0.20, 0),
    upperArmR: rxyz(-0.30, -0.10, -0.10),
    forearmR:  ry( -0.90),
    handR:     rxyz( 0.10,  0.20, 0),
    clavicleL: rz(-0.04),
    clavicleR: rz( 0.04),
  },
};

export const ALL_PROCEDURAL_HOLDS = Object.keys(PROCEDURAL_HOLD_POSES) as ProceduralHoldName[];

/** True if the given hold name has a procedural pose. */
export function isProceduralHold(name: string): name is ProceduralHoldName {
  return name in PROCEDURAL_HOLD_POSES;
}

// ── Limb conflict groups ──────────────────────────────────────────────────
//
// Each hold occupies one or both arm chains (clavicle → upperArm → forearm
// → hand). When the LLM activates a new hold, the runtime releases any
// active hold whose limbs overlap so e.g. `arms_crossed` + `hand_in_pocket
// _right` does not sum into an impossible posture — `arms_crossed` claims
// both arms and would auto-release the pocket hold (or vice versa,
// depending on activation order).
//
// `arms_relaxed` is special: it is the explicit "release everything" hold.
// It claims both arms and authors a zero-offset pose, so activating it is
// equivalent to releasing all current holds while leaving a clean neutral.

export type LimbGroup = 'left_arm' | 'right_arm';

/**
 * Inverse of the LimbGroup → bone-chain mapping. The runtime uses this to
 * know which specific BoneSlots a hold occupies once it's been resolved
 * down to a LimbGroup. Used by the override-during-hold path in figure.ts
 * to wipe the mixer's contribution on these bones while a hold is active.
 *
 * The chain order (clavicle → upperArm → forearm → hand) is the kinematic
 * order from torso outward; nothing in the runtime depends on the order
 * (it's just iterated as a set), but keeping it consistent makes diffs
 * readable.
 */
export const LIMB_TO_BONES: Record<LimbGroup, BoneSlot[]> = {
  left_arm:  ['clavicleL', 'upperArmL', 'forearmL', 'handL'],
  right_arm: ['clavicleR', 'upperArmR', 'forearmR', 'handR'],
};

export const PROCEDURAL_HOLD_LIMBS: Record<ProceduralHoldName, LimbGroup[]> = {
  arms_crossed:              ['left_arm', 'right_arm'],
  hand_on_hip_right:         ['right_arm'],
  hand_on_hip_left:          ['left_arm'],
  hands_on_hips:             ['left_arm', 'right_arm'],
  hands_clasped_front:       ['left_arm', 'right_arm'],
  hands_clasped_behind_back: ['left_arm', 'right_arm'],
  hands_clasped_behind_head: ['left_arm', 'right_arm'],
  hand_in_pocket_right:      ['right_arm'],
  arms_relaxed:              ['left_arm', 'right_arm'],
  // Aliases inherit the same limb claim as their canonical pose.
  hand_on_hip:               ['right_arm'],
  hands_behind_back:         ['left_arm', 'right_arm'],
};

/**
 * Return the names of holds in `active` that share at least one limb with
 * `incoming`. Used by the runtime to know which holds to fade out when a
 * new one comes in.
 */
export function conflictingHolds(
  incoming: ProceduralHoldName,
  active: Iterable<string>,
): string[] {
  const incomingLimbs = new Set(PROCEDURAL_HOLD_LIMBS[incoming]);
  const out: string[] = [];
  for (const name of active) {
    if (name === incoming) continue;
    if (!isProceduralHold(name)) continue;
    const otherLimbs = PROCEDURAL_HOLD_LIMBS[name];
    if (otherLimbs.some((l) => incomingLimbs.has(l))) {
      out.push(name);
    }
  }
  return out;
}

// ── Composition helpers ───────────────────────────────────────────────────

/**
 * In-place add: `acc[slot] += weight * src[slot]` for every axis present
 * on the source. Missing axes default to zero. Mutates `acc` and returns
 * it for chaining.
 */
export function accumulatePose(
  acc: Record<BoneSlot, EulerOffset>,
  src: BonePose,
  weight = 1,
): Record<BoneSlot, EulerOffset> {
  if (weight === 0) return acc;
  for (const key of Object.keys(src) as BoneSlot[]) {
    const o = src[key];
    if (!o) continue;
    const a = acc[key] ?? (acc[key] = { x: 0, y: 0, z: 0 });
    if (o.x) a.x = (a.x ?? 0) + o.x * weight;
    if (o.y) a.y = (a.y ?? 0) + o.y * weight;
    if (o.z) a.z = (a.z ?? 0) + o.z * weight;
  }
  return acc;
}

/** Build a fresh accumulator with all slots present and zeroed. */
export function emptyOffsets(): Record<BoneSlot, EulerOffset> {
  const out = {} as Record<BoneSlot, EulerOffset>;
  for (const slot of ALL_BONE_SLOTS) {
    out[slot] = { x: 0, y: 0, z: 0 };
  }
  return out;
}
